import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Button,
  VStack,
  useToast,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import TipTapEditor from "../common/TipTapEditor";
import axios from "../../axiosConfig";
import InterpretationChecklistManager from "../checklists/InterpretationChecklistManager";

const DEFAULT_LANGUAGE = "en";

const InterpretationEditor = ({ surveyPageId }) => {
  const toast = useToast();
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [description, setDescription] = useState("");
  const [diagnostic, setDiagnostic] = useState("");
  const [interpretationId, setInterpretationId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInterpretation = async () => {
    try {
      const response = await axios.get(`/interpretations`, {
        params: { survey_page_id: surveyPageId, language },
      });

      if (response.data?.id) {
        const interpretation = response.data;
        setInterpretationId(interpretation.id);
        setDescription(interpretation.description || "");
        setDiagnostic(interpretation.diagnostic || "");
      } else {
        setInterpretationId(null);
        setDescription("");
        setDiagnostic("");
      }
    } catch (error) {
      toast({ title: "Error loading interpretation.", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterpretation();
  }, [surveyPageId, language]);

  const handleSaveInterpretation = async () => {
    const payload = {
      survey_page_id: surveyPageId,
      language,
      description,
      diagnostic,
    };

    try {
      let response;
      if (interpretationId) {
        response = await axios.patch(
          `/interpretations/${interpretationId}`,
          payload
        );
      } else {
        response = await axios.post(`/interpretations`, payload);
        setInterpretationId(response.data.id);
      }

      toast({ title: "Interpretation saved.", status: "success" });
      if (!interpretationId) fetchInterpretation();
    } catch {
      toast({ title: "Error saving interpretation.", status: "error" });
    }
  };

  if (loading) return <Spinner size="lg" />;

  return (
    <VStack spacing={6} align="stretch" mt={4}>
      {/* Language */}
      <FormControl>
        <FormLabel>Language</FormLabel>
        <Select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          maxW="200px"
        >
          <option value="en">English</option>
          <option value="sv">Swedish</option>
        </Select>
      </FormControl>

      {/* Description */}
      <FormControl>
        <FormLabel>Description (<em>Cause of the problem</em>)</FormLabel>
        <TipTapEditor content={description} onChange={setDescription} />
      </FormControl>

      {/* Diagnostic Notes */}
      <FormControl>
        <FormLabel>Diagnostic Notes</FormLabel>
        <TipTapEditor content={diagnostic} onChange={setDiagnostic} />
      </FormControl>

      <Divider />

      {/* Diagnostic Checklist Manager */}
      {interpretationId ? (
        <Box>
          <FormLabel fontSize="xl" fontWeight="bold" color="orange.500">
            Diagnostic Checklist
          </FormLabel>
          <InterpretationChecklistManager interpretationId={interpretationId} />
        </Box>
      ) : (
        <Box>
          <Button
            colorScheme="orange"
            variant="outline"
            onClick={handleSaveInterpretation}
          >
            Save the interpretation first to add checklist items
          </Button>
        </Box>
      )}

      <Box>
        <Button
          onClick={handleSaveInterpretation}
          colorScheme="green"
          variant="outline"
          isDisabled={loading}
        >
          Save Interpretation
        </Button>
      </Box>
    </VStack>
  );
};

export default InterpretationEditor;
